import { useState, useCallback, useMemo } from 'react';
import type { Workflow, Project } from '../../types';

export type CanvasTab = 'all' | 'personal' | 'collaboration';

export interface UseCanvasGalleryOptions {
  workflows: Workflow[];
  projects?: Project[];
  onSelectWorkflow: (id: string) => void;
  onDeleteWorkflow: (id: string) => void;
  onRenameWorkflow: (id: string, newTitle: string) => void;
}

export function useCanvasGallery(options: UseCanvasGalleryOptions) {
  const { workflows, projects, onDeleteWorkflow, onRenameWorkflow } = options;
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<CanvasTab>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const displayProjects = useMemo(
    () =>
      projects ||
      workflows.map((w) => ({
        id: w.id,
        name: w.title,
        workflow_data: { nodes: w.nodes, connections: w.connections, groups: w.groups },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        thumbnail: w.thumbnail,
      })),
    [projects, workflows]
  );

  const filteredProjects = useMemo(() => {
    let list = displayProjects;
    if (activeTab === 'personal') {
      list = list.filter((p) => !(p as any).isCollaboration);
    } else if (activeTab === 'collaboration') {
      list = list.filter((p) => (p as any).isCollaboration);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    return list;
  }, [displayProjects, activeTab, searchQuery]);

  const startRename = useCallback((id: string, currentTitle: string) => {
    setEditingId(id);
    setEditingTitle(currentTitle);
  }, []);

  const saveRename = useCallback(
    (id: string) => {
      if (editingTitle.trim()) {
        onRenameWorkflow(id, editingTitle.trim());
      }
      setEditingId(null);
      setEditingTitle('');
    },
    [editingTitle, onRenameWorkflow]
  );

  const cancelRename = useCallback(() => {
    setEditingId(null);
    setEditingTitle('');
  }, []);

  const deleteProject = useCallback(
    (id: string) => {
      onDeleteWorkflow(id);
    },
    [onDeleteWorkflow]
  );

  return {
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    filteredProjects,
    editingId,
    editingTitle,
    setEditingTitle,
    startRename,
    saveRename,
    cancelRename,
    deleteProject,
  };
}
